/*
 * OZ 2026-10-14, v2 2026-10-15: buying on the new product page (the default product template). One owner, one function
 * (AGREED-SPEC 15; Upsells repo docs/developer/interfaces.md 6.1 and theme-integration.md).
 *
 *   Add to cart ------------------+
 *   sticky bar (presses it) ------+--> theme.js ProductVariants (_addToCart / _ozBuy)
 *   Shop Pay, Checkout -----------+        --> window.OzBuy.buy(kind, { section, button })
 *   Apple Pay, Google Pay -----------> the wallet tap listener below, inside the shopper's own tap
 *
 *   OzBuy.buy(kind)        kind: 'cart' | 'shop' | 'checkout'; resolves true when it navigates, false otherwise
 *     1. selections        the variant on screen + every switched-on add-on (.related_product_block_check_input).
 *                          An add-on row drawn by the Upsells app carries data-oz-token, which goes on the cart line
 *                          as the private property _upsell (hidden at checkout, kept on the order; the Discount
 *                          Function and the analytics read it). Items merge by "<variant id>|<token>".
 *     2. upsell step       OzBuy.upsell.decide(ctx) === true -> OzBuy.upsell.open(ctx) (assets/oz-upsells.js, the
 *                          "Complete your order" pop-up), which ends with ctx.proceed(extraItems) (continue the way the
 *                          tapped button would; "No thanks" is ctx.proceed([])) or ctx.cancel() (back, X, Escape:
 *                          nothing is added and OzBuy.buy resolves false). Otherwise straight on.
 *     3. cart              every kind keeps whatever else is in the cart:
 *                          'cart' adds the selections (first taking back lines a given-up express tap left that the
 *                          shopper has since switched off or moved away from);
 *                          'shop' and 'checkout' make sure each selected line is in the cart once
 *     4. go                'cart' -> /cart, 'shop' -> /checkout?payment=shop_pay, 'checkout' -> /checkout
 *
 * The wallet (AGREED-SPEC 15, Dereck 2026-10-15): where the device has Apple Pay or Google Pay, the right-hand button is
 * Shopify's own cart wallet element ([data-oz-wallet], snippets/product-buy-buttons.liquid), and the black Checkout
 * button ('checkout' above) is only its stand-in on devices without either. The wallet's sheet opens only from the
 * shopper's own tap on Apple's or Google's button, and Shopify's button pays for the cart as it is when the sheet
 * starts. So everything happens inside that tap, in a capture listener that runs before the element sees it:
 *   - the upsell step decides at once (kind 'checkout', ctx.wallet true). With a pop-up, the tap stops there and the
 *     pop-up opens; its pinned button is the same wallet (snippets/oz-upsells-popup.liquid), and a tap on it fills the
 *     cart with the pop-up's extras the same way (OzBuy.upsell.walletTap, assets/oz-upsells.js);
 *   - otherwise the cart is filled by the express rules of step 3 through synchronous requests (the same code as the
 *     other kinds, run on the SYNC requester), then the tap goes on to Shopify's button and the sheet opens;
 *   - if the cart cannot be prepared, or the wallet has not drawn its button yet, the tap is stopped and the Checkout
 *     path runs instead (/checkout with the same lines), so a sheet never opens on the wrong cart.
 * watchWallets() gives the spot to the Checkout button when Shopify's element draws no button on the device (Shopify
 * sends Apple Pay to every iPhone browser, but only Safari with Apple Pay can show it).
 *
 * "Once" for the express kinds: each wanted line ("<variant id>|<token>") is made present once. Quantity fixes and
 * take-backs of lines a previous express tap added (remembered for this browser tab, sessionStorage
 * "oz-express-lines", { v: 2, lines: { "<variant id>|<token>": <quantity added by express> } }; a v1 ledger reads as
 * empty) run first, then the missing lines are added in one request. A line is taken back only when the page still
 * offers it as a choice and the shopper has said no: the phone variant they moved away from, a shown in-page row
 * switched off. A pop-up extra cannot be chosen again (the pop-up hides what is in the cart, AGREED-SPEC 6), so its line
 * stays. In-page rows show even what is in the cart (Dereck 2026-10-15), and a row whose line this tab's express tap
 * added comes back switched on (syncRowsWithLedger), so only switching it off takes it back. Line keys change whenever a line
 * changes (https://shopify.dev/docs/api/ajax/reference/cart), and /cart/change.js changes one line per request, so
 * before each change the cart is read again and the line found by variant id plus _upsell, never by a key from an
 * earlier read; a 400 (line gone) reads again and re-plans. Going back from checkout and tapping again never buys two
 * phones, and lines added any other way are never touched.
 *
 * A 422 (something sold out between the page loading and the tap) retries the items one by one: the failures are
 * skipped and reported as oz_upsells:error (add_422); only a failure of the main variant stops the buy.
 * After a successful fill, oz_upsells:add is published for the tokened quantities this fill actually added (new lines
 * and positive quantity fixes), so a second tap that finds its lines already there publishes nothing.
 *
 * Nothing else adds to the cart on this template: snippets/product-info.liquid's add-on scripts and the theme's UFE
 * call are off here (see those files).
 */
(function () {
  'use strict';

  var LEDGER_KEY = 'oz-express-lines';
  var DESTINATION = { cart: '/cart', shop: '/checkout?payment=shop_pay', checkout: '/checkout' };
  var TOKEN_RE = /^u1\.([ipc])\.(\d{1,7})\.(\d{1,7})\.(\d{1,9})$/;
  var EVENT_ITEMS = 50;
  var WALLET_TAG = 'shopify-accelerated-checkout-cart';
  var WALLET_BUTTONS = 'shopify-apple-pay-button, shopify-google-pay-button';
  var WALLET_WAIT = 8000;
  var state = { busy: false, opening: false };

  var OzBuy = window.OzBuy = window.OzBuy || {};
  // assets/oz-upsells.js installs the real step; it may load before or after this file
  OzBuy.upsell = OzBuy.upsell || {
    decide: function () { return false; },
    open: function (ctx) { ctx.proceed([]); }
  };
  OzBuy.navigate = OzBuy.navigate || function (url) { window.location.href = url; };

  function tokenOf(item) { return (item && item.properties && item.properties._upsell) || ''; }
  function keyOf(id, token) { return String(id) + '|' + (token || ''); }
  function itemKey(item) { return keyOf(item.id, tokenOf(item)); }
  function lineKey(line) { return keyOf(line.variant_id, line.properties && line.properties._upsell); }

  /* ---------- events (Upsells interfaces.md 7.1) ---------- */
  function publish(name, data) {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, data);
      }
    } catch (e) { /* analytics must never break buying */ }
  }
  function publishError(code, detail) {
    publish('oz_upsells:error', { v: 1, code: code, detail: String(detail || '').slice(0, 120) });
  }
  // added: [{ token, productId, variantId, quantity }]
  function publishAdded(added) {
    var byPl = {};
    added.forEach(function (a) {
      var m = TOKEN_RE.exec(a.token || '');
      if (!m || !(a.quantity > 0)) { return; }
      (byPl[m[1]] = byPl[m[1]] || []).push([Number(m[2]), Number(m[3]), Number(m[4]), Number(a.productId) || 0, Number(a.variantId), a.quantity]);
    });
    Object.keys(byPl).forEach(function (pl) {
      for (var i = 0; i < byPl[pl].length; i += EVENT_ITEMS) {
        publish('oz_upsells:add', { v: 1, pl: pl, items: byPl[pl].slice(i, i + EVENT_ITEMS) });
      }
    });
  }

  /* ---------- 1. selections ---------- */
  function selectionItems(section) {
    var form = section && section.querySelector('form[action*="/cart/add"]');
    var idInput = form && form.querySelector('[name="id"]');
    if (!idInput || !idInput.value) { return []; }
    var qtyInput = form.querySelector('[name="quantity"]');
    var items = [{ id: Number(idInput.value), quantity: Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1) }];
    section.querySelectorAll('.related_product_block_check_input:checked').forEach(function (input) {
      var id = Number(String(input.value || '').trim());
      if (!id) { return; }
      var item = { id: id, quantity: 1 };
      var token = input.getAttribute('data-oz-token');
      if (token && TOKEN_RE.test(token)) { item.properties = { _upsell: token }; }
      items.push(item);
    });
    return items;
  }

  function mergeItems(items) {
    var byKey = {};
    var order = [];
    items.forEach(function (item) {
      var id = Number(item && item.id);
      if (!id) { return; }
      var quantity = Math.max(1, parseInt(item.quantity, 10) || 1);
      var token = tokenOf(item);
      var key = keyOf(id, token);
      if (!byKey[key]) {
        byKey[key] = { id: id, quantity: 0 };
        if (token) { byKey[key].properties = { _upsell: token }; }
        order.push(key);
      }
      byKey[key].quantity += quantity;
    });
    return order.map(function (key) { return byKey[key]; });
  }

  /* ---------- the express ledger (v2) ---------- */
  function readLedger() {
    try {
      var raw = JSON.parse(window.sessionStorage.getItem(LEDGER_KEY) || 'null');
      if (!raw || raw.v !== 2 || !raw.lines || typeof raw.lines !== 'object') { return {}; }
      var lines = {};
      Object.keys(raw.lines).forEach(function (key) {
        var n = parseInt(raw.lines[key], 10);
        if (n > 0 && /^\d+\|/.test(key)) { lines[key] = n; }
      });
      return lines;
    } catch (e) { return {}; }
  }
  function writeLedger(lines) {
    try {
      if (Object.keys(lines).length) {
        window.sessionStorage.setItem(LEDGER_KEY, JSON.stringify({ v: 2, lines: lines }));
      } else {
        window.sessionStorage.removeItem(LEDGER_KEY);
      }
    } catch (e) { /* private mode: no memory */ }
  }

  /* ---------- requests: fetch (ASYNC) everywhere, XHR (SYNC) inside a wallet tap ----------
   * The cart code below is written once against `io`: ASYNC answers Promises; SYNC answers Now values, thenables that
   * settle at once, so the very same steps run to the end before the wallet tap returns. */
  function httpError(method, url, status, detail) {
    var error = new Error(method + ' ' + url + ' ' + status);
    error.status = status;
    error.detail = detail;
    return error;
  }
  function Now(ok, value) { this.ok = ok; this.value = value; }
  Now.prototype.then = function (onOk, onError) {
    var next = this.ok ? onOk : onError;
    if (!next) { return this; }
    try {
      var out = next(this.value);
      if (out instanceof Now) { return out; }
      if (out && typeof out.then === 'function') { return new Now(false, new Error('async step in a wallet tap')); }
      return new Now(true, out);
    } catch (e) { return new Now(false, e); }
  };
  var ASYNC = {
    resolve: function (value) { return Promise.resolve(value); },
    request: function (method, url, body) {
      return fetch(url, {
        method: method,
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: body ? JSON.stringify(body) : undefined
      }).then(function (response) {
        if (!response.ok) {
          return response.text().then(function (text) { throw httpError(method, url, response.status, text); }, function () { throw httpError(method, url, response.status); });
        }
        return response.json();
      });
    }
  };
  var SYNC = {
    resolve: function (value) { return new Now(true, value); },
    request: function (method, url, body) {
      try {
        var xhr = new XMLHttpRequest();
        xhr.open(method, url, false);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('Accept', 'application/json');
        xhr.send(body ? JSON.stringify(body) : null);
        if (xhr.status < 200 || xhr.status >= 300) { return new Now(false, httpError(method, url, xhr.status, xhr.responseText)); }
        return new Now(true, JSON.parse(xhr.responseText));
      } catch (e) { return new Now(false, e); }
    }
  };

  /* ---------- 3. cart ---------- */
  // One cart read per change, never a cached line key (see the note at the top).
  function Cart(io) { this.io = io; this.cart = null; this.dirty = true; }
  Cart.prototype.read = function (fresh) {
    var self = this;
    if (!fresh && !self.dirty && self.cart) { return self.io.resolve(self.cart); }
    return self.io.request('GET', '/cart.js').then(function (cart) { self.cart = cart; self.dirty = false; return cart; });
  };
  Cart.prototype.find = function (cart, key) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) { if (lineKey(items[i]) === key) { return items[i]; } }
    return null;
  };
  // Sets the line `key` to targetOf(quantity in the cart now). Resolves { have, now, line } (have 0 = no such line).
  Cart.prototype.adjust = function (key, targetOf, tries) {
    var self = this;
    return self.read(false).then(function (cart) {
      var line = self.find(cart, key);
      if (!line || targetOf(line.quantity) === line.quantity) { return { have: line ? line.quantity : 0, now: line ? line.quantity : 0, line: line }; }
      return self.read(true).then(function (fresh) {
        var current = self.find(fresh, key);
        if (!current) { return { have: 0, now: 0, line: null }; }
        var target = targetOf(current.quantity);
        if (target === current.quantity) { return { have: current.quantity, now: current.quantity, line: current }; }
        self.dirty = true;
        return self.io.request('POST', '/cart/change.js', { id: current.key, quantity: target }).then(function () {
          return { have: current.quantity, now: target, line: current };
        }, function (error) {
          // 400: the line is gone (its key changed under us); read again and re-plan this line
          if (error.status === 400 && (tries || 0) < 2) { return self.adjust(key, targetOf, (tries || 0) + 1); }
          throw error;
        });
      });
    });
  };

  // Adds `items` (properties kept). On a 422, retries them one by one; resolves { ok, added } where ok is false only
  // when the main variant (mainKey) could not be added.
  function addItems(io, items, mainKey) {
    if (!items.length) { return io.resolve({ ok: true, added: [] }); }
    var productOf = function (response, item) {
      var list = (response && response.items) || [];
      for (var i = 0; i < list.length; i++) { if (Number(list[i].variant_id) === item.id) { return list[i].product_id; } }
      return 0;
    };
    var record = function (response, list) {
      return list.map(function (item) { return { token: tokenOf(item), productId: productOf(response, item), variantId: item.id, quantity: item.quantity }; });
    };
    return io.request('POST', '/cart/add.js', { items: items }).then(function (response) {
      return { ok: true, added: record(response, items) };
    }, function (error) {
      if (error.status !== 422) { throw error; }
      var added = [];
      var ok = true;
      var chain = io.resolve();
      items.forEach(function (item) {
        chain = chain.then(function () {
          if (!ok) { return null; }
          return io.request('POST', '/cart/add.js', { items: [item] }).then(function (response) {
            added = added.concat(record(response, [item]));
          }, function (one) {
            if (one.status !== 422) { throw one; }
            publishError('add_422', 'variant ' + item.id);
            if (itemKey(item) === mainKey) { ok = false; }
          });
        });
      });
      return chain.then(function () { return { ok: ok, added: added }; });
    });
  }

  // A row whose add-on this tab's express tap put in the cart comes back switched on: it is part of the purchase the
  // shopper left (rows show even what is in the cart since 2026-10-15), so only switching it off takes it back.
  function syncRowsWithLedger() {
    var ledger = readLedger();
    Array.prototype.forEach.call(document.querySelectorAll('.related_product_block_check_input[data-oz-token]'), function (input) {
      var key = String(input.value) + '|' + input.getAttribute('data-oz-token');
      if (ledger[key] > 0 && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }
  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', syncRowsWithLedger); } else { syncRowsWithLedger(); }

  // Whether the page still lets the shopper say yes or no to a ledger line. An untokened line (the phone variant, a
  // legacy add-on row) always: not wanted means they moved away from it or switched it off. A tokened line only while
  // an in-page row with that token is shown and can carry that variant. A pop-up extra ('p' token) or a row left out
  // because its product is already in the cart cannot be chosen again, so its line counts as still wanted.
  function choosable(section, key) {
    var bar = key.indexOf('|');
    var vid = key.slice(0, bar);
    var token = key.slice(bar + 1);
    if (!token) { return true; }
    if (!section) { return false; }
    return Array.prototype.some.call(section.querySelectorAll('.related_product_block_check_input'), function (input) {
      if (input.getAttribute('data-oz-token') !== token) { return false; }
      var row = input.closest('[data-oz-row]') || input.parentNode;
      if (!row || row.closest('[hidden]')) { return false; }
      if (String(input.value) === vid) { return true; }
      var select = row.querySelector('select[data-oz-options]');
      return !!select && Array.prototype.some.call(select.options, function (o) { return o.value === vid && !o.disabled && !o.hidden; });
    });
  }

  // Take back what an earlier express tap added and the shopper has since said no to: ledger keys not in `wantedKeys`
  // that the page still offers as a choice (choosable). Every other ledger line stays in the cart.
  function takeBacks(cart, ledger, wantedKeys, section) {
    var chain = cart.io.resolve();
    Object.keys(ledger).forEach(function (key) {
      if (wantedKeys[key] || !choosable(section, key)) { return; }
      chain = chain.then(function () {
        var ours = ledger[key];
        return cart.adjust(key, function (have) { return have - Math.min(ours, have); }).then(function () {
          delete ledger[key];
          writeLedger(ledger);
        });
      });
    });
    return chain;
  }

  function fillCart(kind, items, section, io) {
    var mainKey = items.length ? itemKey(items[0]) : '';
    var cart = new Cart(io);
    var ledger = readLedger();
    if (kind === 'cart') {
      // take back what a given-up express tap left, add the selections, and the cart is the shopper's now
      return takeBacks(cart, ledger, {}, section).then(function () { return addItems(io, items, mainKey); }).then(function (result) {
        if (result.ok) { writeLedger({}); }
        return result;
      });
    }
    var wanted = {};
    items.forEach(function (item) { wanted[itemKey(item)] = item.quantity; });
    var added = [];
    var missing = [];
    // In-page rows show even what is already in the cart (Dereck 2026-10-15: "they might get another phone"). The phone
    // stays "once"; an add-on line becomes what the cart held apart from this tab's express taps plus what is wanted
    // now, so a second phone gets its own battery. When the same phone was already in the cart before (not by an
    // express tap), it is the same purchase again and its add-ons stay "once" too.
    var samePhone = false;
    var chain = cart.read(false).then(function (current) {
      var line = cart.find(current, mainKey);
      samePhone = !!line && line.quantity - Math.min(ledger[mainKey] || 0, line.quantity) > 0;
    });
    function targetFor(item, key) {
      if (key === mainKey || samePhone) {
        return function (have) { return have > 0 && have < item.quantity ? item.quantity : have; };
      }
      return function (have) { return Math.max(have, have - Math.min(ledger[key] || 0, have) + item.quantity); };
    }
    // (1) take-backs and quantity fixes, one line at a time, each after a fresh read
    chain = chain.then(function () { return takeBacks(cart, ledger, wanted, section); });
    items.forEach(function (item) {
      var key = itemKey(item);
      chain = chain.then(function () {
        return cart.adjust(key, targetFor(item, key)).then(function (r) {
          if (!r.have) { missing.push(item); return; }
          var ours = Math.min(ledger[key] || 0, r.have) + (r.now - r.have);
          if (ours > 0) { ledger[key] = ours; } else { delete ledger[key]; }
          if (r.now > r.have) { added.push({ token: tokenOf(item), productId: r.line && r.line.product_id, variantId: item.id, quantity: r.now - r.have }); }
          writeLedger(ledger);
        });
      });
    });
    // (2) then the missing lines, in one request
    return chain.then(function () {
      return addItems(io, missing, mainKey);
    }).then(function (result) {
      result.added.forEach(function (a) { ledger[keyOf(a.variantId, a.token)] = a.quantity; });
      writeLedger(ledger);
      return { ok: result.ok, added: added.concat(result.added) };
    });
  }

  /* ---------- the one buy function ---------- */
  // Builds the ctx and runs the upsell step at once, so a wallet tap knows inside the tap whether the pop-up took it.
  // Returns { promise, ctx, popup }; ctx is null when nothing can start (busy, or nothing selected).
  function begin(kind, options) {
    var run = { promise: Promise.resolve(false), ctx: null, popup: false };
    if (!DESTINATION[kind] || state.busy || state.opening) { return run; }
    var section = options.section || document.querySelector('[data-section-type="product"]');
    var selections = selectionItems(section);
    if (!selections.length) { return run; }
    var button = options.button;
    var settled = false;
    var resolveRun = null;
    run.promise = new Promise(function (resolve) { resolveRun = resolve; });
    var finish = function (value) {
      if (settled) { return; }
      settled = true;
      resolveRun(value);
    };
    var release = function () {
      state.busy = false;
      state.opening = false;
      if (button) { button.removeAttribute('aria-busy'); }
    };
    var ctx = {
      kind: kind,
      // the tap came from the Apple Pay / Google Pay button: the pop-up's pinned button is then the wallet
      wallet: !!options.wallet,
      walletTag: options.walletTag || '',
      section: section,
      selections: selections,
      // 4. continue: from here, or from the upsell pop-up with the extras the shopper picked
      // Returns a promise: true once the browser is on its way to the destination, false when nothing was done
      // (the pop-up keeps itself open with a spinner until then, Dereck 2026-10-16)
      proceed: function (extraItems) {
        if (settled || state.busy) { return Promise.resolve(false); }
        state.opening = false;
        state.busy = true;
        if (button) { button.setAttribute('aria-busy', 'true'); }
        var items = mergeItems(selections.concat(extraItems || []));
        return fillCart(kind, items, section, ASYNC).then(function (result) {
          if (!result.ok) {
            release();
            if (window.console) { console.warn('[oz-buy] ' + kind + ': the selected variant could not be added'); }
            finish(false);
            return false;
          }
          publishAdded(result.added.filter(function (a) { return a.token; }));
          OzBuy.navigate(DESTINATION[kind]);
          finish(true);
          return true;
        }, function (error) {
          release();
          if (window.console) { console.warn('[oz-buy] ' + kind + ' failed:', error); }
          finish(false);
          return false;
        });
      },
      // close, add nothing: OzBuy.buy resolves false and the buttons work again
      cancel: function () {
        if (settled || state.busy) { return; }
        release();
        finish(false);
      },
      // The wallet, inside the shopper's tap: the express fill with synchronous requests. true when the cart holds the
      // lines and the tap may go on to the wallet's sheet; the pop-up (if any) stays open behind the sheet, so a
      // cancelled sheet comes back to it.
      walletFill: function (extraItems) {
        if (settled || state.busy) { return false; }
        var items = mergeItems(selections.concat(extraItems || []));
        var result = fillCart(kind, items, section, SYNC);
        if (!result.ok || !result.value.ok) {
          if (window.console) { console.warn('[oz-buy] wallet: the cart could not be prepared, Checkout instead:', result.ok ? 'the selected variant could not be added' : result.value); }
          return false;
        }
        publishAdded(result.value.added.filter(function (a) { return a.token; }));
        return true;
      }
    };
    run.ctx = ctx;
    // 2. upsell step; a fault in it never blocks buying
    var open = false;
    try { open = OzBuy.upsell.decide(ctx) === true; } catch (e) { open = false; }
    if (!open) {
      // a wallet tap with no pop-up fills the cart itself, inside the tap (onWalletTap)
      if (!ctx.wallet) { ctx.proceed([]); }
      return run;
    }
    state.opening = true;
    run.popup = true;
    try {
      OzBuy.upsell.open(ctx);
    } catch (e) {
      state.opening = false;
      run.popup = false;
      if (window.console) { console.warn('[oz-buy] upsell step failed:', e); }
      if (!ctx.wallet) { ctx.proceed([]); }
    }
    return run;
  }

  OzBuy.buy = function (kind, options) {
    return begin(kind, options || {}).promise;
  };

  /* ---------- the wallet button ---------- */
  function walletButton(holder) { return holder.querySelector(WALLET_BUTTONS); }

  // The tap on Apple Pay or Google Pay (see the note at the top). Runs in the capture phase on document, before
  // Shopify's element; stopping the event there means no sheet opens.
  function onWalletTap(event) {
    var target = event.target;
    var holder = target && target.closest ? target.closest('[data-oz-wallet]') : null;
    if (!holder) { return; }
    var stop = function () {
      event.preventDefault();
      event.stopImmediatePropagation();
    };
    var popupRoot = holder.closest('[data-oz-upsells-popup]');
    if (popupRoot) {
      // the open pop-up's pinned wallet: its extras go in with the selections, then the sheet opens over the pop-up
      var tap = OzBuy.upsell.walletTap ? OzBuy.upsell.walletTap() : null;
      if (!tap) { stop(); return; }
      if (!target.closest(WALLET_BUTTONS) || !tap.ctx.walletFill(tap.items)) {
        stop();
        tap.fallback();
      }
      return;
    }
    var section = holder.closest('[data-section-type="product"]');
    if (state.busy || state.opening) { stop(); return; }
    if (!target.closest(WALLET_BUTTONS)) {
      // the wallet is still loading (or drew nothing here): the Checkout path, which has the same pop-up
      stop();
      OzBuy.buy('checkout', { section: section });
      return;
    }
    // which wallet was tapped, so the pop-up's pinned foot shows that one only (Dereck 2026-10-16: never both on an iPhone)
    var tapped = target.closest(WALLET_BUTTONS);
    var run = begin('checkout', { section: section, wallet: true, walletTag: tapped ? tapped.tagName.toLowerCase() : '' });
    if (!run.ctx) { stop(); return; }
    if (run.popup) { stop(); return; } // the pop-up took the tap; its pinned wallet takes the next one
    if (!run.ctx.walletFill([])) {
      stop();
      run.ctx.proceed([]);
    }
    // otherwise the tap goes on to Shopify's button, which opens the sheet for the cart as it is now
  }
  document.addEventListener('click', onWalletTap, true);

  // The Checkout button stands in for the wallet where Shopify's element draws no button: .has-wallet comes off the
  // express row once the element has finished loading empty, or when it never loads. A button that turns up later
  // takes the spot back.
  function watchWallets() {
    document.querySelectorAll('[data-oz-express] [data-oz-wallet]').forEach(function (holder) {
      if (holder.__ozWatched) { return; }
      holder.__ozWatched = true;
      var row = holder.closest('[data-oz-express]');
      var settled = function () {
        if (walletButton(holder)) { row.classList.add('has-wallet'); return true; }
        var defined = !!(window.customElements && window.customElements.get(WALLET_TAG));
        if (defined && !holder.querySelector('.wallet-cart-button__skeleton')) { row.classList.remove('has-wallet'); return true; }
        return false;
      };
      if ('MutationObserver' in window) {
        new MutationObserver(settled).observe(holder, { childList: true, subtree: true });
      }
      settled();
      window.setTimeout(function () { if (!settled()) { row.classList.remove('has-wallet'); } }, WALLET_WAIT);
    });
  }

  // back from checkout or the cart with the browser's back button (page restored from memory): buttons work again
  window.addEventListener('pageshow', function () {
    state.busy = false;
    state.opening = false;
    document.querySelectorAll('[aria-busy="true"]').forEach(function (el) { el.removeAttribute('aria-busy'); });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchWallets);
  } else {
    watchWallets();
  }
})();
